import { Link } from "react-router-dom";
import { ArrowRight, Check, Wind } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import { IMAGES, BREEZE_MASTER } from "@/data/content";

const AirConditioning = () => (
    <div data-testid="air-conditioning-page">
        <Seo
            title="Breeze Master — Air Conditioning Wholesaler & Retailer Since 1970 | Kavraj Spaces"
            description="Breeze Master by Kavraj Spaces — authorised distributor and retailer of Napoleon, Mitsubishi Electric, Mitsubishi Heavy Duty, Panasonic and OG Heavy Duty air conditioning. Split, cassette, ductable, VRV/VRF and concealed ducted AC for homes, businesses and HVAC contractors."
        />

        <PageHero
            eyebrow="Air Conditioning"
            title="Breeze Master"
            intro={BREEZE_MASTER.intro}
            testid="ac-hero"
        />

        <section className="border-b border-bronze/15 bg-cream" data-testid="ac-badge-section">
            <div className="mx-auto max-w-7xl px-6 py-10 sm:px-12 lg:px-20">
                <Reveal>
                    <p
                        data-testid="ac-since-badge"
                        className="inline-block border-y-2 border-bronze py-3 font-serif text-xl italic tracking-tight text-bronze sm:text-2xl"
                    >
                        {BREEZE_MASTER.badge}
                    </p>
                </Reveal>
            </div>
        </section>

        <section className="bg-cream" data-testid="ac-intro">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-32">
                <Reveal>
                    <img
                        src={IMAGES.acCassette.url}
                        alt={IMAGES.acCassette.alt}
                        loading="lazy"
                        className="h-full w-full object-cover"
                    />
                </Reveal>
                <Reveal delay={120}>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        Climate, considered
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Cooling that disappears into a well-designed space.
                    </h2>
                    <p className="mt-6 text-base leading-relaxed text-stone2">
                        For over five decades, Breeze Master has supplied air-conditioning systems to
                        the people who build and inhabit Delhi&rsquo;s spaces — from a single split
                        unit for a family home to VRV systems for entire commercial floors.
                    </p>
                    <p className="mt-5 text-base leading-relaxed text-stone2">
                        Because Kavraj Spaces also designs and builds interiors, your climate system
                        is planned with the space — concealed, ducted and specified correctly from
                        the first drawing.
                    </p>
                    <div className="mt-9 flex flex-wrap gap-4">
                        <Link
                            to="/contact"
                            data-testid="ac-enquire-btn"
                            className="inline-flex items-center justify-center gap-2 bg-bronze px-7 py-3.5 text-sm font-semibold tracking-wide text-cream transition-[background-color,transform] duration-300 hover:bg-bronze-light active:scale-[0.97]"
                        >
                            Enquire Now
                            <ArrowRight size={16} aria-hidden="true" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="ac-brands">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        Our brands
                    </p>
                    <h2 className="mt-4 max-w-2xl font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Authorised for the names that matter.
                    </h2>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
                    {BREEZE_MASTER.brands.map((brand, i) => (
                        <Reveal key={brand.name} delay={i * 90}>
                            <div
                                className="flex h-full flex-col border border-bronze/15 bg-cream p-6 transition-colors duration-300 hover:border-bronze/40"
                                data-testid={`ac-brand-${i}`}
                            >
                                <Wind size={18} className="text-bronze" aria-hidden="true" />
                                <h3 className="mt-4 font-serif text-xl leading-snug text-ink">
                                    {brand.name}
                                </h3>
                                <p className="mt-2 flex-1 text-sm leading-relaxed text-stone2">
                                    {brand.text}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        <section className="bg-cream" data-testid="ac-products">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-32">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 lg:gap-16">
                    <Reveal className="lg:col-span-1">
                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                            Products & systems
                        </p>
                        <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                            The right system for every space.
                        </h2>
                        <p className="mt-5 text-base leading-relaxed text-stone2">
                            From a single room to an entire building, we recommend and supply the
                            configuration that fits the space, the usage and the budget.
                        </p>
                        <img
                            src={IMAGES.acUnit.url}
                            alt={IMAGES.acUnit.alt}
                            loading="lazy"
                            className="mt-10 hidden w-full object-cover lg:block"
                        />
                    </Reveal>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:col-span-2">
                        {BREEZE_MASTER.products.map((product, i) => (
                            <Reveal key={product.name} delay={(i % 2) * 100}>
                                <div
                                    className="h-full border border-bronze/15 bg-cream-muted p-7 transition-colors duration-300 hover:border-bronze/40"
                                    data-testid={`ac-product-${i}`}
                                >
                                    <span className="font-serif text-sm italic text-bronze">
                                        0{i + 1}
                                    </span>
                                    <h3 className="mt-3 font-serif text-xl leading-snug text-ink">
                                        {product.name}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-stone2">
                                        {product.text}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>

        <section className="border-y border-bronze/15 bg-cream-muted" data-testid="ac-offerings">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 sm:px-12 lg:grid-cols-2 lg:gap-20 lg:px-20 lg:py-28">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze">
                        What we offer
                    </p>
                    <h2 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl">
                        Supply, guidance and support — end to end.
                    </h2>
                </Reveal>
                <Reveal delay={120}>
                    <ul className="space-y-4">
                        {BREEZE_MASTER.offerings.map((offering, i) => (
                            <li
                                key={offering}
                                data-testid={`ac-offering-${i}`}
                                className="flex items-start gap-3 border-b border-bronze/10 pb-4 text-base leading-relaxed text-stone2"
                            >
                                <Check
                                    size={18}
                                    className="mt-1 shrink-0 text-bronze"
                                    aria-hidden="true"
                                />
                                {offering}
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </section>

        <section className="bg-charcoal" data-testid="ac-mission">
            <div className="mx-auto max-w-7xl px-6 py-20 sm:px-12 lg:px-20 lg:py-28">
                <Reveal>
                    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-bronze-light">
                        Our mission
                    </p>
                    <blockquote className="mt-6 max-w-3xl font-serif text-2xl leading-snug tracking-tight text-cream sm:text-3xl lg:text-4xl">
                        {BREEZE_MASTER.mission}
                    </blockquote>
                    <div className="mt-10">
                        <Link
                            to="/contact"
                            data-testid="ac-mission-enquire-btn"
                            className="inline-flex items-center justify-center gap-2 bg-bronze px-7 py-3.5 text-sm font-semibold tracking-wide text-cream transition-[background-color,transform] duration-300 hover:bg-bronze-light active:scale-[0.97]"
                        >
                            Enquire Now
                            <ArrowRight size={16} aria-hidden="true" />
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    </div>
);

export default AirConditioning;
